/**
 * Composable for managing Visenze image search functionality.
 *
 * @description
 * Provides reactive state for visual search status and generates a unique user identifier.
 *
 * @returns {Object} An object containing visual search state and user identifier.
 */
export const useVisenzeImageSearch = () => {
  /**
   * Reactive state to track the status of visual search.
   * @type {Ref<boolean>}
   * @default false
   */
  const vSearchActive = useState('visual-search-status', () => false)

  /**
   * Generates or retrieves a unique user identifier.
   *
   * @description
   * - Attempts to retrieve an existing UID from localStorage
   * - If no UID exists, generates a new random UUID-like identifier
   * - Uses a custom generation algorithm that creates a pseudo-random string
   *
   * @type {ComputedRef<string>}
   * @returns {string} A unique user identifier
   */
  const uid = computed(
    () =>
      (window &&
        window.localStorage &&
        window.localStorage.getItem('va-uid')) ||
      (() => {
        let e = new Date().getTime()
        return 'xxxxxxxx.xxxx.4xxx.yxxx.xxxxxxxxxxxx'.replace(/[xy]/g, x => {
          const t = (e + 16 * Math.random()) % 16 | 0
          e = Math.floor(e / 16)
          return (x === 'x' ? t : (3 & t) | 8).toString(16)
        })
      })()
  )

  return {
    /** Reactive state for visual search status */
    vSearchActive,
    /** Unique user identifier */
    uid,
  }
}
